<template>
  <div class="detail-container estimate-detail">
    <div class="estimate-detail-header d-flex justify-content-between align-items-start mb-3 gap-3">
      <div>
        <h1 class="page-title mb-1">見積書詳細</h1>
        <div v-if="estimate" class="text-muted">{{ estimate.estimate_no }}</div>
      </div>
      <div v-if="estimate" class="d-flex flex-wrap justify-content-end gap-2 action-buttons">
        <router-link
          v-if="estimate.permissions?.can_edit"
          class="btn btn-success"
          :to="{
            name: 'EstimateEdit',
            params: { id: estimate.id },
            query: { ...route.query, edit_origin: 'detail' }
          }"
        >
          編集
        </router-link>
        <button
          v-else-if="estimate.del_flg !== 1"
          class="btn btn-success"
          type="button"
          disabled
          title="承認済みまたは作業作成済みの見積は編集できません"
        >
          編集
        </button>
        <button
          v-if="estimate.del_flg !== 1"
          class="btn btn-outline-success"
          type="button"
          :disabled="isDuplicating || !estimate.permissions?.can_copy"
          @click="copyCurrent"
        >
          別案件として複製
        </button>
        <button
          v-if="estimate.permissions?.can_revise && estimate.is_latest_version"
          class="btn btn-outline-warning"
          type="button"
          :disabled="isDuplicating"
          @click="reviseCurrent"
        >
          改訂版を作成
        </button>
        <button
          v-if="estimate.del_flg !== 1"
          class="btn btn-outline-dark"
          type="button"
          :disabled="isGeneratingPdf || !pdfOutputSupported || !estimate.permissions?.can_output_pdf"
          :title="pdfOutputSupported ? '' : pdfOutputUnsupportedReason"
          @click="generatePdf"
        >
          {{ isGeneratingPdf ? 'PDF作成中...' : 'PDF出力' }}
        </button>
        <button
          v-if="estimate.permissions?.can_request_approval"
          class="btn btn-primary"
          type="button"
          @click="requestApproval"
        >
          承認申請
        </button>
        <button
          v-if="estimate.permissions?.can_approve"
          class="btn btn-primary"
          type="button"
          @click="approve"
        >
          承認
        </button>
        <button
          v-if="estimate.permissions?.can_reject"
          class="btn btn-outline-danger"
          type="button"
          @click="reject"
        >
          差戻し
        </button>
        <button
          v-if="estimate.permissions?.can_unlock_approval"
          class="btn btn-outline-warning"
          type="button"
          @click="unlockApproval"
        >
          承認ロック解除
        </button>
        <button
          v-if="estimate.permissions?.can_present"
          class="btn btn-outline-primary"
          type="button"
          @click="present"
        >
          顧客提示
        </button>
        <button
          v-if="estimate.permissions?.can_order"
          class="btn btn-warning"
          type="button"
          @click="order"
        >
          受注
        </button>
        <router-link
          v-if="estimate.permissions?.can_convert && estimate.is_latest_version"
          class="btn btn-success"
          :to="{
            name: 'EstimateWorkConversionPreview',
            params: { id: estimate.id },
            query: route.query
          }"
        >
          次へ：作業作成
        </router-link>
        <button
          v-if="estimate.permissions?.can_mark_lost"
          class="btn btn-outline-secondary"
          type="button"
          @click="markLost"
        >
          失注
        </button>
        <button
          v-if="estimate.permissions?.can_delete"
          class="btn btn-danger"
          type="button"
          data-bs-toggle="modal"
          data-bs-target="#deleteModal"
        >
          削除
        </button>
        <button
          v-else-if="estimate.del_flg !== 1"
          class="btn btn-danger"
          type="button"
          disabled
          title="承認済みまたは作業作成済みの見積は削除できません"
        >
          削除
        </button>
        <button
          v-if="estimate.permissions?.can_restore"
          class="btn btn-primary"
          type="button"
          @click="restoreCurrent"
        >
          復元
        </button>
      </div>
    </div>

    <div v-if="errorMessages.length" class="alert alert-danger">
      <div v-for="message in errorMessages" :key="message">{{ message }}</div>
    </div>

    <template v-if="estimate">
      <div class="table-responsive mb-3 estimate-summary-table">
        <table class="table table-bordered mb-0">
          <thead>
            <tr class="table-info">
              <th class="text-center">作成日時</th>
              <th class="text-center">更新日時</th>
              <th class="text-center">PDF出力日時</th>
              <th class="text-center">作業作成状況</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="text-center text-nowrap">{{ formatFullDateTime(estimate.created_at) }}</td>
              <td class="text-center text-nowrap">{{ formatFullDateTime(estimate.updated_at) }}</td>
              <td class="text-center text-nowrap">{{ formatFullDateTime(latestPdfOutputAt) }}</td>
              <td class="text-center text-nowrap">{{ workCreationStatus }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="estimate.del_flg === 1" class="alert alert-secondary">
        この見積は削除済みです。内容の参照のみ可能です。
      </div>

      <div v-else-if="estimate.is_latest_version === false" class="alert alert-secondary">
        この見積は旧版です。内容の参照、版間比較、PDFの出力・再ダウンロードのみ可能です。
      </div>

      <div
        v-if="isAwaitingWorkCreation"
        class="alert alert-success work-creation-guidance"
      >
        <div>
          <strong>受注登録が完了しています。</strong>
          <div v-if="estimate.permissions?.can_convert">
            次は、この見積内容から作業を作成してください。
          </div>
          <div v-else>
            次は作業作成です。作業作成を行える担当者へ依頼してください。
          </div>
        </div>
        <router-link
          v-if="estimate.permissions?.can_convert"
          class="btn btn-success"
          :to="{
            name: 'EstimateWorkConversionPreview',
            params: { id: estimate.id },
            query: route.query
          }"
        >
          次へ：この見積から作業を作成する
        </router-link>
      </div>

      <div v-if="isPostOrderRevisionAvailable" class="alert alert-info revision-guidance">
        <strong>受注後の追加・変更は「改訂版を作成」を使用してください。</strong>
        同じ見積番号の次の版を下書きで作成します。元の版と作成済みの作業はそのまま保持されます。
      </div>

      <div class="card mb-3 estimate-notes-card">
        <div class="card-header fw-bold">備考</div>
        <div class="card-body">
          <p class="small text-muted mb-3">
            ※備考内容はPDFには表示されません。
          </p>
          <dl class="row mb-0">
            <dt class="col-sm-3">見積書備考</dt>
            <dd class="col-sm-9 preserve-lines">{{ estimate.general_notes || '-' }}</dd>
            <dt class="col-sm-3">社内備考</dt>
            <dd class="col-sm-9 preserve-lines mb-0">{{ estimate.internal_notes || '-' }}</dd>
          </dl>
        </div>
      </div>

      <div
        v-if="estimate.versions?.length > 1 || estimate.source_estimate_id"
        class="card mb-3"
      >
        <div class="card-header fw-bold">改訂情報</div>
        <div class="card-body p-0">
          <div v-if="estimate.source_estimate_id" class="source-estimate-link">
            作成元：
            <router-link
              :to="{
                name: 'EstimateDetail',
                params: { id: estimate.source_estimate_id },
                query: route.query
              }"
            >
              見積ID {{ estimate.source_estimate_id }}
            </router-link>
          </div>
          <div v-if="comparableVersions.length" class="diff-controls">
            <label class="form-label mb-0" for="estimate-diff-base">比較元の版</label>
            <select
              id="estimate-diff-base"
              v-model="diffBaseId"
              class="form-select form-select-sm"
            >
              <option
                v-for="version in comparableVersions"
                :key="version.id"
                :value="version.id"
              >
                {{ versionLabel(version.revision_no) }}（{{ version.issue_date ? formatIsoDate(version.issue_date) : '発行日なし' }}）
              </option>
            </select>
            <span>→ {{ versionLabel(estimate.revision_no) }}</span>
            <button
              class="btn btn-sm btn-primary"
              type="button"
              :disabled="!diffBaseId || isDiffLoading"
              @click="loadDiff"
            >
              {{ isDiffLoading ? '比較中...' : '差分を表示' }}
            </button>
          </div>
          <div v-if="estimate.versions?.length > 1" class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0 version-table">
              <thead>
                <tr>
                  <th>版</th>
                  <th>発行日</th>
                  <th>承認状態</th>
                  <th>営業状態</th>
                  <th>税込合計</th>
                  <th>作成者</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="version in estimate.versions"
                  :key="version.id"
                  :class="{
                    'table-primary': version.current,
                    'table-secondary': version.del_flg === 1
                  }"
                >
                  <td>
                    {{ versionLabel(version.revision_no) }}
                    <span v-if="version.current" class="badge text-bg-primary ms-1">表示中</span>
                  </td>
                  <td>{{ formatIsoDate(version.issue_date) }}</td>
                  <td>{{ approvalStatusLabels[version.approval_status] }}</td>
                  <td>{{ salesStatusLabels[version.sales_status] }}</td>
                  <td class="text-end">{{ formatCurrency(version.total_amount) }}</td>
                  <td>{{ version.created_by_user?.name || '-' }}</td>
                  <td class="text-center">
                    <router-link
                      v-if="!version.current && version.del_flg !== 1"
                      class="btn btn-sm btn-outline-primary"
                      :to="{
                        name: 'EstimateDetail',
                        params: { id: version.id },
                        query: route.query
                      }"
                    >
                      表示
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="estimateDiff" class="card mb-3 diff-card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <span class="fw-bold">
            版間差分：
            {{ versionLabel(estimateDiff.base.revision_no) }}
            →
            {{ versionLabel(estimateDiff.target.revision_no) }}
          </span>
          <button class="btn btn-sm btn-outline-secondary" type="button" @click="estimateDiff = null">
            閉じる
          </button>
        </div>
        <div class="card-body">
          <div class="d-flex flex-wrap gap-2 mb-3">
            <span class="badge text-bg-dark">
              変更合計 {{ estimateDiff.summary.total_change_count }}
            </span>
            <span class="badge text-bg-info">
              基本情報 {{ estimateDiff.summary.header_changed_count }}
            </span>
            <span class="badge text-bg-success">
              追加 {{ totalDiffCount('added') }}
            </span>
            <span class="badge text-bg-danger">
              削除 {{ totalDiffCount('removed') }}
            </span>
            <span class="badge text-bg-warning">
              変更 {{ totalDiffCount('modified') }}
            </span>
          </div>

          <section v-if="estimateDiff.header_changes.length" class="mb-4">
            <h2 class="diff-heading">基本情報・金額</h2>
            <div class="table-responsive">
              <table class="table table-sm table-bordered diff-table">
                <thead>
                  <tr><th>項目</th><th>変更前</th><th>変更後</th></tr>
                </thead>
                <tbody>
                  <tr v-for="change in estimateDiff.header_changes" :key="change.field">
                    <th>{{ change.label }}</th>
                    <td class="diff-before">{{ diffValue(change.before) }}</td>
                    <td class="diff-after">{{ diffValue(change.after) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section v-if="estimateDiff.section_changes.length" class="mb-4">
            <h2 class="diff-heading">セクション</h2>
            <div
              v-for="change in estimateDiff.section_changes"
              :key="change.lineage_key"
              class="diff-record"
            >
              <div class="diff-record-title">
                <span :class="['badge', diffBadgeClass(change.change_type)]">
                  {{ diffTypeLabel(change.change_type) }}
                </span>
                <strong>{{ change.name }}</strong>
              </div>
              <table
                v-if="change.change_type === 'modified'"
                class="table table-sm table-bordered diff-table mb-0"
              >
                <tbody>
                  <tr v-for="field in change.changes" :key="field.field">
                    <th>{{ field.label }}</th>
                    <td class="diff-before">{{ diffValue(field.before) }}</td>
                    <td class="diff-after">{{ diffValue(field.after) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="diff-details">
                <span v-for="field in change.details" :key="field.field">
                  <strong>{{ field.label }}：</strong>{{ diffValue(field.value) }}
                </span>
              </div>
            </div>
          </section>

          <section v-if="estimateDiff.line_changes.length">
            <h2 class="diff-heading">明細</h2>
            <div
              v-for="change in estimateDiff.line_changes"
              :key="change.lineage_key"
              class="diff-record"
            >
              <div class="diff-record-title">
                <span :class="['badge', diffBadgeClass(change.change_type)]">
                  {{ diffTypeLabel(change.change_type) }}
                </span>
                <strong>{{ change.name }}</strong>
              </div>
              <table
                v-if="change.change_type === 'modified'"
                class="table table-sm table-bordered diff-table mb-0"
              >
                <tbody>
                  <tr v-for="field in change.changes" :key="field.field">
                    <th>{{ field.label }}</th>
                    <td class="diff-before">{{ diffValue(field.before) }}</td>
                    <td class="diff-after">{{ diffValue(field.after) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="diff-details">
                <span v-for="field in change.details" :key="field.field">
                  <strong>{{ field.label }}：</strong>{{ diffValue(field.value) }}
                </span>
              </div>
            </div>
          </section>

          <div
            v-if="estimateDiff.summary.total_change_count === 0"
            class="text-center text-muted py-3"
          >
            比較した版の見積内容に差分はありません。
          </div>
        </div>
      </div>

      <div v-if="estimate.work_links?.length" class="card mb-3">
        <div class="card-header fw-bold">作業変換結果</div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0">
              <thead>
                <tr>
                  <th>変換元</th>
                  <th>変換先</th>
                  <th>変換金額</th>
                  <th>変換日時</th>
                  <th>変換者</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="link in estimate.work_links" :key="link.id">
                  <td>
                    {{ link.estimate_section?.section_nm ||
                      link.estimate_line?.item_nm || '-' }}
                  </td>
                  <td>
                    <template v-if="link.annual_work">
                      年間予定：{{ link.annual_work.work_content }}
                      （{{ link.annual_work.work_year }}年）
                    </template>
                    <template v-else-if="link.temporary_work">
                      臨時作業：{{ link.temporary_work.work_content }}
                    </template>
                  </td>
                  <td class="text-end">{{ formatCurrency(link.source_amount) }}</td>
                  <td class="text-nowrap">{{ formatDateTime(link.converted_at) }}</td>
                  <td>{{ link.converted_by_user?.name || '-' }}</td>
                  <td class="text-center text-nowrap">
                    <router-link
                      v-if="link.annual_work && estimate.building?.id"
                      class="btn btn-sm btn-outline-primary"
                      :to="{
                        name: 'AnnualWorkScheduleEdit',
                        params: {
                          id: link.annual_work.id,
                          building_id: estimate.building.id,
                          start_month: 1
                        }
                      }"
                    >
                      年間予定を編集
                    </router-link>
                    <router-link
                      v-else-if="link.temporary_work"
                      class="btn btn-sm btn-outline-primary"
                      :to="{
                        name: 'TemporaryWorkDetail',
                        params: { id: link.temporary_work.id }
                      }"
                    >
                      臨時作業を表示
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <EstimateDocumentViewer :estimate="estimate" />

      <div class="btn-area">
        <router-link class="btn btn-secondary" :to="{ name: listRouteName, query: route.query }">
          一覧へ戻る
        </router-link>
      </div>
    </template>

    <div v-else-if="isLoading" class="text-center py-5">読み込み中...</div>

    <EstimateStatusTransitionModal
      ref="statusTransitionModal"
      :is-submitting="isTransitioning"
      @confirm="submitStatusTransition"
    />

    <ConfirmationModal
      ref="confirmationModal"
      :is-submitting="isConfirmingAction"
      @confirm="submitConfirmedAction"
    />

    <DeleteConfirmationModal
      v-if="estimate"
      :paramsId="estimate.id"
      @confirmDelete="remove"
    />
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  approveEstimate,
  copyEstimate,
  createEstimatePdfHistory,
  deleteEstimate,
  getEstimate,
  getEstimateDiff,
  markEstimateLost,
  orderEstimate,
  presentEstimate,
  rejectEstimate,
  restoreEstimate,
  reviseEstimate,
  requestEstimateApproval,
  unlockEstimateApproval
} from '@/api/axios';
import EstimateStatusTransitionModal from '@/components/estimates/EstimateStatusTransitionModal.vue';
import EstimateDocumentViewer from '@/components/estimates/EstimateDocumentViewer.vue';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import {
  approvalStatusLabels,
  extractApiErrors,
  formatCurrency,
  formatDateTime,
  formatIsoDate,
  formatRevisionLabel,
  salesStatusLabels
} from '@/utils/estimates';
import { createEstimatePdf, downloadBlob, isEstimatePdfSupported } from '@/utils/estimatePdf';

export default {
  components: {
    ConfirmationModal,
    DeleteConfirmationModal,
    EstimateDocumentViewer,
    EstimateStatusTransitionModal
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const estimate = ref(null);
    const errorMessages = ref([]);
    const isLoading = ref(false);
    const isDuplicating = ref(false);
    const isGeneratingPdf = ref(false);
    const isDiffLoading = ref(false);
    const diffBaseId = ref('');
    const estimateDiff = ref(null);
    const statusTransitionModal = ref(null);
    const confirmationModal = ref(null);
    const isTransitioning = ref(false);
    const isConfirmingAction = computed(() => isDuplicating.value || isTransitioning.value);
    const latestPdfOutputAt = computed(() =>
      estimate.value?.pdf_histories?.[0]?.generated_at || null
    );
    const workCreationStatus = computed(() => {
      if (!estimate.value) return '-';
      if (estimate.value.conversion_status === 'converted') return '作成済み';
      if (Number(estimate.value.del_flg) === 1) return '対象外（削除済み）';
      if (estimate.value.is_latest_version === false) return '対象外（旧版）';
      if (estimate.value.sales_status === 'lost') return '対象外（失注）';
      if (estimate.value.sales_status === 'ordered') return '作成待ち';
      return '受注前';
    });
    const isAwaitingWorkCreation = computed(() =>
      estimate.value?.sales_status === 'ordered' &&
      estimate.value?.conversion_status === 'not_converted' &&
      estimate.value?.is_latest_version === true &&
      Number(estimate.value?.del_flg) !== 1
    );
    const isPostOrderRevisionAvailable = computed(() =>
      estimate.value?.sales_status === 'ordered' &&
      estimate.value?.is_latest_version === true &&
      estimate.value?.permissions?.can_revise === true
    );
    const listRouteName = computed(() =>
      route.query.list_origin === 'approval_pending'
        ? 'EstimateApprovalPendingList'
        : 'EstimateList'
    );

    const comparableVersions = computed(() => {
      if (!estimate.value) return [];
      return (estimate.value.versions || [])
        .filter(version =>
          version.del_flg !== 1 &&
          Number(version.revision_no) < Number(estimate.value.revision_no)
        )
        .sort((a, b) => Number(b.revision_no) - Number(a.revision_no));
    });
    const pdfOutputSupported = computed(() => isEstimatePdfSupported(estimate.value));
    const pdfOutputUnsupportedReason = computed(() =>
      '現在の出力形式または言語には対応していません。'
    );

    const generatePdf = async () => {
      if (!estimate.value || isGeneratingPdf.value || !pdfOutputSupported.value) return;

      try {
        isGeneratingPdf.value = true;
        errorMessages.value = [];

        const languageType = estimate.value.default_language_type;
        const { blob: pdfBlob, filename } = await createEstimatePdf(estimate.value);

        const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });
        const response = await createEstimatePdfHistory(
          estimate.value.id,
          pdfFile,
          estimate.value.output_layout_type,
          languageType
        );
        estimate.value.pdf_histories = [
          response.data.pdf_history,
          ...(estimate.value.pdf_histories || [])
        ];
        downloadBlob(pdfBlob, filename);
        store.dispatch('setFlashMessage', {
          message: '見積書PDFを作成し、出力履歴に保存しました',
          type: 'success'
        });
      } catch (error) {
        const messages = error?.response ? extractApiErrors(error) : [error.message];
        errorMessages.value = messages;
        store.dispatch('setFlashMessage', {
          message: messages[0] || 'PDFの作成に失敗しました',
          type: 'danger'
        });
      } finally {
        isGeneratingPdf.value = false;
      }
    };

    const fetchEstimate = async () => {
      try {
        isLoading.value = true;
        errorMessages.value = [];
        const response = await getEstimate(route.params.id, { include_deleted: true });
        estimate.value = response.data.estimate;
        estimateDiff.value = null;
        diffBaseId.value = comparableVersions.value[0]?.id || '';
      } catch (error) {
        errorMessages.value = extractApiErrors(error);
      } finally {
        isLoading.value = false;
      }
    };

    const loadDiff = async () => {
      if (!estimate.value || !diffBaseId.value || isDiffLoading.value) return;

      try {
        isDiffLoading.value = true;
        errorMessages.value = [];
        const response = await getEstimateDiff(estimate.value.id, diffBaseId.value);
        estimateDiff.value = response.data.diff;
      } catch (error) {
        estimateDiff.value = null;
        errorMessages.value = extractApiErrors(error);
      } finally {
        isDiffLoading.value = false;
      }
    };

    const runTransition = async (request, successMessage) => {
      if (!estimate.value || isTransitioning.value) return false;
      try {
        isTransitioning.value = true;
        errorMessages.value = [];
        const response = await request(estimate.value);
        estimate.value = response.data.estimate;
        store.dispatch('setFlashMessage', { message: successMessage, type: 'success' });
        return true;
      } catch (error) {
        const messages = extractApiErrors(error);
        errorMessages.value = messages;
        store.dispatch('setFlashMessage', {
          message: messages[0] || '状態変更に失敗しました',
          type: error?.response?.status === 409 ? 'warning' : 'danger'
        });
        return false;
      } finally {
        isTransitioning.value = false;
      }
    };

    const createDerivedEstimate = async (request, successMessage) => {
      if (!estimate.value || isDuplicating.value) return false;

      try {
        isDuplicating.value = true;
        errorMessages.value = [];
        const response = await request(estimate.value);
        const created = response.data.estimate;
        store.dispatch('setFlashMessage', {
          message: successMessage(created),
          type: 'success'
        });
        await router.push({
          name: 'EstimateEdit',
          params: { id: created.id },
          query: route.query
        });
        return true;
      } catch (error) {
        const messages = extractApiErrors(error);
        errorMessages.value = messages;
        store.dispatch('setFlashMessage', {
          message: messages[0] || '見積の複製に失敗しました',
          type: error?.response?.status === 409 ? 'warning' : 'danger'
        });
        return false;
      } finally {
        isDuplicating.value = false;
      }
    };

    const copyCurrent = () => {
      confirmationModal.value?.show({
        action: 'copy',
        title: '別案件への見積複製確認',
        description: 'この操作は、顧客・建物・案件が異なる見積の再利用用です。\n受注後の追加・変更では「改訂版を作成」を使用してください。\n新しい見積番号で下書きを作成してよろしいですか？',
        confirmLabel: '別案件として複製する',
        submittingLabel: '複製中...',
        confirmButtonClass: 'btn-success'
      });
    };

    const reviseCurrent = () => {
      const description = estimate.value?.sales_status === 'ordered'
        ? 'この受注済み見積へ追加・変更内容を反映するため、改訂版を作成します。\n新しい見積番号は発行せず、同じ見積番号の次の版を下書きで作成します。\n元の版と作成済みの作業は変更されません。'
        : 'この見積の改訂版を作成します。\n新しい見積番号は発行せず、同じ見積番号の次の版を下書きで作成します。';
      confirmationModal.value?.show({
        action: 'revise',
        title: '改訂版作成確認',
        description,
        confirmLabel: '改訂版を作成する',
        submittingLabel: '作成中...',
        confirmButtonClass: 'btn-warning'
      });
    };

    const requestApproval = () => {
      statusTransitionModal.value?.show({
        action: 'requestApproval',
        title: '承認申請確認',
        description: 'この見積を承認申請します。承認者へのコメントがあれば入力してください。',
        inputLabel: '承認者へのコメント（任意）',
        confirmLabel: '申請する',
        submittingLabel: '申請中...',
        confirmButtonClass: 'btn-primary'
      });
    };
    const approve = () => {
      statusTransitionModal.value?.show({
        action: 'approve',
        title: '見積承認確認',
        description: 'この見積を承認します。承認コメントがあれば入力してください。',
        inputLabel: '承認コメント（任意）',
        confirmLabel: '承認する',
        submittingLabel: '承認中...',
        confirmButtonClass: 'btn-primary'
      });
    };
    const reject = () => {
      statusTransitionModal.value?.show({
        action: 'reject',
        title: '見積差戻し確認',
        description: 'この見積を差し戻します。差戻し理由を入力してください。',
        inputLabel: '差戻し理由',
        isRequired: true,
        requiredMessage: '差戻し理由を入力してください',
        confirmLabel: '差し戻す',
        submittingLabel: '差戻し中...',
        confirmButtonClass: 'btn-danger'
      });
    };
    const present = () => {
      confirmationModal.value?.show({
        action: 'present',
        title: '顧客提示確認',
        description: 'この見積を顧客提示済みにします。よろしいですか？',
        confirmLabel: '顧客提示済みにする',
        submittingLabel: '処理中...',
        confirmButtonClass: 'btn-primary'
      });
    };
    const unlockApproval = () => {
      statusTransitionModal.value?.show({
        action: 'unlockApproval',
        title: '承認ロック解除確認',
        description: '承認済みの見積を下書きへ戻して編集可能にします。解除理由を入力してください。',
        inputLabel: 'ロック解除理由',
        isRequired: true,
        requiredMessage: 'ロック解除理由を入力してください',
        confirmLabel: 'ロックを解除する',
        submittingLabel: '解除中...',
        confirmButtonClass: 'btn-warning'
      });
    };
    const order = () => {
      confirmationModal.value?.show({
        action: 'order',
        title: '受注登録確認',
        description: 'この見積を受注として登録します。よろしいですか？',
        confirmLabel: '受注登録する',
        submittingLabel: '登録中...',
        confirmButtonClass: 'btn-warning'
      });
    };
    const markLost = () => {
      statusTransitionModal.value?.show({
        action: 'markLost',
        title: '失注登録確認',
        description: 'この見積を失注として登録します。分類・詳細はいずれも任意です。',
        selectLabel: '失注理由の分類（任意）',
        selectOptions: [
          '予算・金額',
          '時期・納期',
          '他社決定',
          '案件中止・延期',
          'その他'
        ].map(value => ({ value, label: value })),
        inputLabel: '失注理由の詳細（任意）',
        confirmLabel: '失注登録する',
        submittingLabel: '登録中...',
        confirmButtonClass: 'btn-danger'
      });
    };

    const restoreCurrent = () => {
      confirmationModal.value?.show({
        action: 'restore',
        title: '見積復元確認',
        description: 'この見積を復元して、通常の一覧へ戻します。よろしいですか？',
        confirmLabel: '復元する',
        submittingLabel: '復元中...',
        confirmButtonClass: 'btn-primary'
      });
    };

    const submitConfirmedAction = async ({ action }) => {
      const actions = {
        copy: () => createDerivedEstimate(
          current => copyEstimate(current.id, current.lock_version),
          created => `見積 ${created.estimate_no} を複製しました`
        ),
        revise: () => createDerivedEstimate(
          current => reviseEstimate(current.id, current.lock_version),
          created => `見積 ${created.estimate_no} ${formatRevisionLabel(created.revision_no)}を作成しました`
        ),
        present: () => runTransition(
          current => presentEstimate(current.id, current.lock_version),
          '顧客提示済みにしました'
        ),
        order: () => runTransition(
          current => orderEstimate(current.id, current.lock_version),
          '受注を登録しました。続けて作業を作成してください'
        ),
        restore: () => runTransition(
          current => restoreEstimate(current.id, current.lock_version),
          '見積を復元しました'
        )
      };
      const execute = actions[action];
      if (!execute) return;

      await execute();
      await nextTick();
      confirmationModal.value?.closeModal();
    };

    const submitStatusTransition = async ({ action, value, selectValue }) => {
      const transitions = {
        requestApproval: {
          request: current => requestEstimateApproval(current.id, current.lock_version, value),
          successMessage: '承認申請しました',
          refreshApprovalPendingCount: true
        },
        approve: {
          request: current => approveEstimate(current.id, current.lock_version, value),
          successMessage: '見積を承認しました',
          refreshApprovalPendingCount: true
        },
        reject: {
          request: current => rejectEstimate(current.id, current.lock_version, value),
          successMessage: '見積を差し戻しました',
          refreshApprovalPendingCount: true
        },
        unlockApproval: {
          request: current => unlockEstimateApproval(current.id, current.lock_version, value),
          successMessage: '承認ロックを解除し、下書きへ戻しました'
        },
        markLost: {
          request: current => markEstimateLost(current.id, current.lock_version, value, selectValue),
          successMessage: '失注を登録しました'
        }
      };
      const transition = transitions[action];
      if (!transition) return;

      const succeeded = await runTransition(transition.request, transition.successMessage);
      if (succeeded) {
        if (transition.refreshApprovalPendingCount) {
          store.dispatch('requestApprovalPendingCountRefresh');
        }
        await nextTick();
        statusTransitionModal.value?.closeModal();
      }
    };

    const remove = async (estimateId) => {
      if (!estimate.value || estimate.value.id !== estimateId) return;
      try {
        await deleteEstimate(estimateId, estimate.value.lock_version);
        store.dispatch('setFlashMessage', { message: '見積を削除しました', type: 'success' });
        router.push({ name: listRouteName.value, query: route.query });
      } catch (error) {
        errorMessages.value = extractApiErrors(error);
      }
    };

    const versionLabel = revisionNo => formatRevisionLabel(revisionNo);
    const diffTypeLabel = changeType => ({
      added: '追加',
      removed: '削除',
      modified: '変更'
    })[changeType] || changeType;
    const diffBadgeClass = changeType => ({
      added: 'text-bg-success',
      removed: 'text-bg-danger',
      modified: 'text-bg-warning'
    })[changeType] || 'text-bg-secondary';
    const diffValue = value => {
      if (value === null || value === undefined || value === '') return '（未設定）';
      return String(value);
    };
    const totalDiffCount = changeType => {
      if (!estimateDiff.value) return 0;
      const summary = estimateDiff.value.summary;
      return Number(summary[`section_${changeType}_count`] || 0) +
        Number(summary[`line_${changeType}_count`] || 0);
    };
    const formatFullDateTime = value => {
      if (!value) return '-';

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);

      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ` +
        `${date.getHours()}時${date.getMinutes()}分${date.getSeconds()}秒`;
    };

    onMounted(() => {
      document.title = '見積書詳細';
    });
    watch(() => route.params.id, fetchEstimate, { immediate: true });

    return {
      route,
      estimate,
      errorMessages,
      isLoading,
      isDuplicating,
      isGeneratingPdf,
      isDiffLoading,
      diffBaseId,
      estimateDiff,
      statusTransitionModal,
      confirmationModal,
      isTransitioning,
      isConfirmingAction,
      latestPdfOutputAt,
      workCreationStatus,
      isAwaitingWorkCreation,
      isPostOrderRevisionAvailable,
      listRouteName,
      comparableVersions,
      approvalStatusLabels,
      salesStatusLabels,
      formatCurrency,
      formatDateTime,
      formatFullDateTime,
      formatIsoDate,
      versionLabel,
      diffTypeLabel,
      diffBadgeClass,
      diffValue,
      totalDiffCount,
      loadDiff,
      generatePdf,
      pdfOutputSupported,
      pdfOutputUnsupportedReason,
      copyCurrent,
      reviseCurrent,
      requestApproval,
      approve,
      reject,
      unlockApproval,
      present,
      order,
      markLost,
      restoreCurrent,
      submitConfirmedAction,
      submitStatusTransition,
      remove
    };
  }
};
</script>

<style scoped>
.estimate-detail {
  box-sizing: border-box;
  width: 95%;
  max-width: 1500px;
}

.action-buttons {
  max-width: 760px;
}

.work-creation-guidance {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: space-between;
  border-left: 6px solid #198754;
}

.work-creation-guidance .btn {
  flex: 0 0 auto;
  font-weight: 700;
}

.preserve-lines {
  white-space: pre-wrap;
}

.source-estimate-link {
  padding: 10px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.diff-controls {
  display: grid;
  grid-template-columns: auto minmax(220px, 360px) auto auto;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  background: #fff8e5;
  border-bottom: 1px solid #dee2e6;
}

.version-table {
  min-width: 850px;
  font-size: 13px;
}

.version-table thead th {
  background: #eef4f8;
}

.diff-heading {
  margin-bottom: 8px;
  padding-left: 8px;
  border-left: 4px solid #003366;
  font-size: 17px;
}

.diff-record {
  margin-bottom: 12px;
  border: 1px solid #d7dee5;
  border-radius: 4px;
  overflow: hidden;
}

.diff-record-title {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px 10px;
  background: #f5f7f9;
}

.diff-table {
  font-size: 13px;
}

.diff-table th {
  width: 22%;
  background: #f7f8fa;
}

.diff-before {
  width: 39%;
  white-space: pre-wrap;
  background: #fff1f1 !important;
}

.diff-after {
  width: 39%;
  white-space: pre-wrap;
  background: #effaf2 !important;
}

.diff-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 6px 16px;
  padding: 10px;
  font-size: 13px;
}

@media (max-width: 900px) {
  .estimate-detail {
    width: calc(100% - 16px);
    margin: 8px auto;
    padding: 12px;
  }

  .estimate-detail-header {
    flex-direction: column;
  }

  .action-buttons {
    max-width: none;
    justify-content: flex-start !important;
  }

  .work-creation-guidance {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }

  .diff-controls {
    grid-template-columns: 1fr;
  }
}
</style>
